import {
  ShieldCheck, CheckCircle, Users, Sparkle, Info,
} from "@phosphor-icons/react";

/**
 * <TrustBadge /> — the canonical labelling system for every piece of content
 * on NaturePulse. Replaces the ambiguous "Verified" label with four clearly
 * defined categories per the AI-audit recommendations:
 *
 *   authoritative  → government/scientific data (NOAA, IPCC, USGS, …)
 *   expert         → community observation confirmed by a domain expert
 *   community      → ordinary community submission (default for observations)
 *   ai-assisted    → summary/profile drafted by AI (may be fact-checked or not)
 *
 * Optional props let you cite the source and last-retrieved timestamp so
 * clicking a badge can eventually surface provenance. See stats overlay.
 */
const VARIANTS = {
  authoritative: {
    label: "Authoritative Data",
    tooltip: "Sourced from a government or scientific organization",
    Icon: ShieldCheck,
    className: "bg-[#0A5C5F]/12 text-[#0A5C5F] border border-[#0A5C5F]/30",
  },
  expert: {
    label: "Expert Confirmed",
    tooltip: "Reviewed and confirmed by a domain expert",
    Icon: CheckCircle,
    className: "bg-[#3A6E2A]/12 text-[#3A6E2A] border border-[#3A6E2A]/30",
  },
  community: {
    label: "Community Observation",
    tooltip: "Submitted by a NaturePulse community member",
    Icon: Users,
    className: "bg-[#B85F2A]/12 text-[#B85F2A] border border-[#B85F2A]/30",
  },
  "ai-assisted": {
    label: "AI-Assisted Summary",
    tooltip: "Drafted with AI assistance — sources listed",
    Icon: Sparkle,
    className: "bg-[#5B49A8]/12 text-[#5B49A8] border border-[#5B49A8]/30",
  },
};

export default function TrustBadge({
  kind = "community",
  size = "sm",         // "xs" | "sm" | "md"
  source,              // optional { org: "NOAA", dataset: "GHCN-M v4", url, retrievedAt }
  className = "",
  compact = false,
  testId,
}) {
  const v = VARIANTS[kind] || VARIANTS.community;
  const px = size === "xs" ? "px-1.5 py-0.5 text-[9px] tracking-[0.18em]"
          : size === "md" ? "px-2.5 py-1 text-[11px] tracking-[0.22em]"
                          : "px-2 py-0.5 text-[10px] tracking-[0.2em]";
  const iconSize = size === "xs" ? 10 : size === "md" ? 13 : 11;
  const label = compact ? "" : v.label;
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full font-mono-tech uppercase whitespace-nowrap ${v.className} ${px} ${className}`}
      title={source ? `${v.tooltip} · Source: ${source.org || "unknown"}${source.retrievedAt ? ` · ${new Date(source.retrievedAt).toLocaleDateString()}` : ""}` : v.tooltip}
      data-testid={testId || `trust-badge-${kind}`}
      role="img"
      aria-label={v.label}
    >
      <v.Icon size={iconSize} weight="fill" />
      {label && <span>{label}</span>}
      {source?.url && (
        <a
          href={source.url}
          target="_blank"
          rel="noreferrer"
          onClick={(e) => e.stopPropagation()}
          className="inline-flex items-center opacity-70 hover:opacity-100"
          title={source.org ? `Open source (${source.org})` : "Open source"}
          aria-label="View source"
        >
          <Info size={iconSize - 1} weight="bold" />
        </a>
      )}
    </span>
  );
}
